import { useCallback, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { useIsAuthQuery } from '@hook/useIsAuth/graphql.v4.generated';
import { login } from '@urls';
import { UNKNOWN_ERROR_MESSAGE } from '@constants';
import { useAppContext } from '@contexts/AppContext';
import { useFlagsWithLoadingState } from '@hook/flagHook';
import { notifyError } from '@utils/notifications';

import { useGoogleOneTapAuthMutation } from './gql/graphql.v5.generated';
import { CredentialResponse, IdConfiguration, MomentListener } from './types';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (input: IdConfiguration) => void,
          prompt: (momentListener?: MomentListener) => void,
          disableAutoSelect: () => void;
          storeCredential: (
            credential: { id: string; password: string },
            callback?: () => void,
          ) => void,
          cancel: () => void,
          revoke: (accessToken: string, done: () => void) => void,
        },
      };
    };
  }
}

interface Props {
  promptContainerId?: string,
  isAutoSelect?: boolean,
  cancelOnOutsideClick?: boolean,
}

export const GoogleOneTapAuthContainer: React.FC<Props> = ({
  promptContainerId,
  isAutoSelect = false,
  cancelOnOutsideClick = true,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);

  const { loading: flagsLoading, flags } = useFlagsWithLoadingState();
  const { isOutlet, isBot } = useAppContext();
  const authRouteMatch = useRouteMatch(login);

  const { data: userData, loading: userLoading } = useIsAuthQuery({
    context: {
      v4: true,
      ignoreAuthError: true,
    },
    skip: isBot
  });

  const [googleAuth] = useGoogleOneTapAuthMutation({
    context: { v5: true },
  });

  const isEnabled = (
    !!GOOGLE_CLIENT_ID
    && !userLoading
    && !userData?.viewer
    && !isOutlet
    && !authRouteMatch
    && !flagsLoading
    && flags.SHAF19434GoogleOneTapLoginEnabled
  );

  const handleLogin = useCallback(async (credentials: CredentialResponse) => {
    if (credentials.credential) {
      try {
        const { data } = await googleAuth({
          variables: {
            token: credentials.credential,
          },
        });
  
        if (data?.googleAuth.user?.id) {
          return window.location.reload();
        }
  
        return notifyError(UNKNOWN_ERROR_MESSAGE());
      } catch {
        notifyError(UNKNOWN_ERROR_MESSAGE());
      }
    } else {
      notifyError(UNKNOWN_ERROR_MESSAGE());
    }
  }, [googleAuth]);

  const handleScriptLoad = useCallback(() => {
    if (window.google?.accounts?.id) {
      window?.google?.accounts?.id?.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleLogin,
        cancel_on_tap_outside: cancelOnOutsideClick,
        prompt_parent_id: promptContainerId,
        auto_select: isAutoSelect,
      });

      setIsInitialized(true);
    }
  }, [
    cancelOnOutsideClick,
    handleLogin,
    isAutoSelect,
    promptContainerId,
  ]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = handleScriptLoad;

    if (isEnabled) {
      document.body.appendChild(script);
    }

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [handleScriptLoad, isEnabled]);

  useEffect(() => {
    if (isEnabled && isInitialized) {
      window?.google?.accounts?.id?.prompt();
    } else if (isInitialized) {
      window.google?.accounts?.id?.cancel();
    }
  }, [isEnabled, isInitialized]);

  return null;
};
